// The identity vendor, confined to one module.
//
// `session.ts` promised that it is "the only thing that knows where the token lives", and
// `SignIn.tsx` promised that the vendor's hosted flow would change nothing else in this
// package. This module is how both stay true: Clerk signs the person in and mints
// short-lived session tokens, and everything downstream keeps reading `session.token()`
// exactly as before — this module's refresh loop writes each fresh token into the store
// before the old one expires.
//
// `VITE_CLERK_PUBLISHABLE_KEY` is a build-time constant like `VITE_MOR_API`, and it is
// the publishable key — made to be shipped to browsers, secret to nobody. Signing in
// needs it: left unset, nothing here activates and the signed-out screens say that
// sign-in isn't set up for this build.
//
// HOW THE VENDOR LOADS, and why it is a script tag and not an import. The npm package's
// module build (`dist/clerk.mjs`) ships without the mountable interactive components —
// `mountSignIn` on that build throws "Clerk was not loaded with Ui components". The
// components live in hot-loaded scripts (`clerk.browser.js` plus, since v6, a separate
// `@clerk/ui` script) served from THIS INSTANCE'S OWN frontend-API domain — the
// publishable key encodes that domain, so nothing here names a hostname. Third-party
// script tags are exactly what this repository is suspicious of; these are first-party
// to the session they manage — the sign-in they render talks to that same domain — and
// they load only on a build that configured a key. Do not "fix" this back to
// `import('@clerk/clerk-js')`; only the type comes from that package now, and the
// loading itself goes through Clerk's own `@clerk/shared` helpers rather than a
// hand-rolled tag, so the URL shapes and readiness checks stay the vendor's problem.

import type { Clerk } from '@clerk/clerk-js';

import { session } from './session.js';

const KEY: string = (import.meta.env['VITE_CLERK_PUBLISHABLE_KEY'] ?? '').trim();

/** Clerk tokens live for a minute; rewriting at 50s means no request carries a dead one. */
const REFRESH_MS = 50_000;

/**
 * Who is signed in, as far as `AccountMenu.tsx` is concerned: the two fields Clerk's own
 * `User` resource carries for display, `fullName` and the address on `primaryEmailAddress`
 * (verified against the installed `@clerk/clerk-js` types — `User.d.ts` declares both as
 * plain properties, not methods). Neither is ever `null` while somebody is signed in with
 * this vendor, but the type says otherwise, so a component reading this store gets the
 * honest optional rather than a component-side assumption about Clerk's data.
 */
export type Identity = { name: string | null; email: string | null };

const NO_ONE: Identity = { name: null, email: null };

const identityListeners = new Set<() => void>();
let identity: Identity = NO_ONE;

function setIdentity(next: Identity): void {
  identity = next;
  for (const listener of identityListeners) {
    listener();
  }
}

/** Subscribable, exactly as `session.ts` and `invite-stash.ts` are. */
export function subscribeIdentity(listener: () => void): () => void {
  identityListeners.add(listener);
  return () => identityListeners.delete(listener);
}

export function identitySnapshot(): Identity {
  return identity;
}

export function clerkConfigured(): boolean {
  return KEY !== '';
}

/**
 * Loads the vendor's two scripts and resolves with the instance on `window`.
 *
 * Two, not one: since v6 the interactive components live in a separate hot-loaded
 * `@clerk/ui` script, and `mountSignIn` on a core that loaded without it throws "Clerk
 * was not loaded with Ui components". The helpers are Clerk's own — they build the
 * instance-domain URLs from the publishable key, pin versions, and poll for readiness —
 * so the URL shapes and the readiness definitions stay the vendor's problem.
 */
async function injectVendorScript(): Promise<Clerk> {
  const { loadClerkJSScript, loadClerkUIScript } =
    await import('@clerk/shared/loadClerkJsScript');
  await loadClerkJSScript({ publishableKey: KEY });
  await loadClerkUIScript({ publishableKey: KEY });
  const holder = window as Window & { Clerk?: Clerk; __internal_ClerkUICtor?: unknown };
  if (holder.Clerk === undefined) {
    throw new Error('The sign-in scripts loaded but left no instance behind.');
  }
  if (holder.__internal_ClerkUICtor === undefined) {
    throw new Error('The sign-in UI script loaded but left no constructor behind.');
  }
  // The core does not look for the UI constructor on `window` — the UI script leaves it
  // there, and `load()` only builds components from an option. Verified against the
  // served build: components come from `options.ui.ClerkUI` and nowhere else.
  return holder.Clerk;
}

/** What `injectVendorScript` found, threaded into `load()` by the one caller below. */
function uiCtor(): { ClerkUI: unknown } {
  return {
    ClerkUI: (window as Window & { __internal_ClerkUICtor?: unknown })
      .__internal_ClerkUICtor,
  };
}

let loading: Promise<Clerk> | null = null;

/** The loaded vendor, one instance per tab. `null` when no key was built in. */
export function getClerk(): Promise<Clerk> | null {
  if (!clerkConfigured()) {
    return null;
  }
  loading ??= (async () => {
    try {
      const clerk = await injectVendorScript();
      // `ui` is how the hot-loaded UI constructor reaches the core; the load-options
      // type lags the served build, hence the cast.
      await clerk.load({ ui: uiCtor() } as Parameters<Clerk['load']>[0]);
      return clerk;
    } catch (error) {
      // Recoverable: the next caller starts over instead of inheriting this rejection.
      loading = null;
      throw error;
    }
  })();
  return loading;
}

/**
 * Keeps `session.ts` holding a live token for as long as the vendor has a session.
 *
 * Signed out is the one state written eagerly; a failed refresh is not — a network blip
 * must degrade to a 401 on the next request, not to the interface logging someone out.
 */
// Set for the duration of `logOut()`'s own vendor call. Without it, a `push()` run by
// the interval below, or by `addListener` firing partway through the vendor's own
// sign-out, can still find `clerk.session` non-null — vendor-internal timing this file
// has no visibility into — and quietly sign the departing person back in underneath
// `logOut()`.
let loggingOut = false;

export function keepSessionFresh(): void {
  const vendor = getClerk();
  if (vendor === null) {
    return;
  }
  void vendor
    .then((clerk) => {
      const push = async (): Promise<void> => {
        if (loggingOut) {
          return;
        }
        if (clerk.session === null || clerk.session === undefined) {
          if (clerk.loaded) {
            session.signOut();
            setIdentity(NO_ONE);
          }
          return;
        }
        // Set before the token fetch, not after: identity is read straight off the
        // resource already held in memory, nothing async about it, and a slow or
        // failing `getToken()` below must not leave `AccountMenu.tsx` showing nobody.
        setIdentity({
          name: clerk.user?.fullName ?? null,
          email: clerk.user?.primaryEmailAddress?.emailAddress ?? null,
        });
        try {
          const token = await clerk.session.getToken();
          if (token !== null && token !== '') {
            session.signIn(token);
          }
        } catch {
          // Keep the current token; it may outlive the blip.
        }
      };
      clerk.addListener(() => {
        void push();
      });
      void push();
      setInterval(() => {
        void push();
      }, REFRESH_MS);
    })
    .catch(() => {
      // The vendor never arrived, so there is no session to keep fresh. The sign-in
      // screen is where that failure is told to the person; here it would be noise.
    });
}

/**
 * The one user-facing way to sign out — `AccountMenu.tsx`'s "Log out".
 *
 * Three things happen, and all matter. First, synchronously and unconditionally: the
 * local session and identity are cleared, so the interface acts signed-out immediately
 * and a later sign-in never briefly shows the previous person's name. Second,
 * best-effort: if a vendor instance is loaded, it is told to sign out too —
 * `.catch(() => {})` matches the rejection handling above, so a failed vendor call
 * surfaces as nothing rather than an unhandled rejection. That step is not optional
 * decoration: without it, `keepSessionFresh`'s own 50-second `push()` would still find
 * the vendor's session alive and quietly sign the token back in underneath the person
 * who just logged out. Third, `loggingOut` holds `push()` off entirely for as long as
 * that vendor call is in flight, because the vendor's own sign-out is not instantaneous
 * either, and `push()` running on `clerk.session` mid-transition is the same failure by
 * a narrower door.
 */
export function logOut(): void {
  session.signOut();
  setIdentity(NO_ONE);
  const vendor = getClerk();
  if (vendor === null) {
    return;
  }
  loggingOut = true;
  void vendor
    .then((clerk) => clerk.signOut())
    .catch(() => {})
    .finally(() => {
      loggingOut = false;
    });
}
